import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link, Stack, Tooltip, Typography } from '@mui/material';
import CopyableText from 'components/atoms/CopyableText';
import { getJobStatusDisplayLabel, JobStatusChip } from 'components/Pages/Jobs/JobStatusChip';
import { IngestJobSummary } from 'interfaces/ingestJob';
import { JobStatus } from 'interfaces/job';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { formatDateTimeForUser } from 'utils/dateTime';

interface IngestJobStatusCellProps {
  job: IngestJobSummary | null;
  jobUrl: string | null;
  rowLabel: string;
}

const IngestJobStatusCell = ({ job, jobUrl, rowLabel }: IngestJobStatusCellProps) => {
  if (!job) {
    return <Typography variant="body2">—</Typography>;
  }

  const jobIdTooltip = (
    <CopyableText
      variant="caption"
      textForCopy={job.id}
      tooltipText="Copy ingest job ID"
      copyWithButton="right"
      copyButtonVisibility="always"
      copyButtonProps={{
        size: 'small',
        'aria-label': `Copy ingest job ID for ${rowLabel}`,
      }}
      copyIconProps={{ fontSize: 'small' }}
      sx={{ fontFamily: 'monospace' }}
    >
      {job.id}
    </CopyableText>
  );
  const statusLabel = getJobStatusDisplayLabel(job.status || undefined);
  const statusContent = (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <JobStatusChip status={job.status || undefined} label={statusLabel} />
      {jobUrl ? <OpenInNewIcon color="action" sx={{ fontSize: 16 }} /> : null}
    </Stack>
  );

  return (
    <Stack spacing={0.25} sx={{ minWidth: 0, width: '100%' }}>
      <Typography variant="caption" color="text.secondary">
        {job.startedAt ? formatDateTimeForUser(job.startedAt) : 'Start date unavailable'}
      </Typography>
      <Tooltip title={jobIdTooltip} placement="top" enterDelay={300}>
        <span>
          {jobUrl ? (
            <Link
              component={RouterLink}
              to={jobUrl}
              underline="none"
              aria-label={`Open ${statusLabel.toLowerCase()} ingest job for ${rowLabel}`}
              sx={{ display: 'inline-flex' }}
            >
              {statusContent}
            </Link>
          ) : (
            statusContent
          )}
        </span>
      </Tooltip>
      {job.status !== JobStatus.Completed ? (
        <Typography variant="caption" color="text.secondary" noWrap title={job.statusMessage || undefined}>
          {job.statusMessage || 'No status details'}
        </Typography>
      ) : null}
    </Stack>
  );
};

export default IngestJobStatusCell;
